import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { restClient } from "@/src/services/restClient";
const articlesAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.title.localeCompare(b.title)
});
const initialState = articlesAdapter.getInitialState({
  status: "idle",
  error: null
});
const toMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error) return error.message;
  return fallback;
};
export const fetchArticles = createAsyncThunk("articles/fetchAll", async (_, {
  rejectWithValue
}) => {
  try {
    return await restClient.get("/articles");
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to load articles"));
  }
});
export const createArticleThunk = createAsyncThunk("articles/create", async (payload, {
  rejectWithValue
}) => {
  try {
    return await restClient.post("/articles", payload);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to create article"));
  }
});
export const updateArticleThunk = createAsyncThunk("articles/update", async ({
  id,
  patch
}, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/articles/${id}`, patch);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to update article"));
  }
});
export const deleteArticleThunk = createAsyncThunk("articles/delete", async (id, {
  rejectWithValue
}) => {
  try {
    await restClient.delete(`/articles/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to delete article"));
  }
});
const articlesSlice = createSlice({
  name: "articles",
  initialState,
  reducers: {
    clearArticlesError(state) {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchArticles.pending, state => {
      state.status = "loading";
      state.error = null;
    }).addCase(fetchArticles.fulfilled, (state, action) => {
      state.status = "succeeded";
      articlesAdapter.setAll(state, action.payload);
    }).addCase(fetchArticles.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload ?? action.error.message ?? null;
    }).addCase(createArticleThunk.fulfilled, (state, action) => {
      articlesAdapter.upsertOne(state, action.payload);
    }).addCase(updateArticleThunk.fulfilled, (state, action) => {
      articlesAdapter.upsertOne(state, action.payload);
    }).addCase(deleteArticleThunk.fulfilled, (state, action) => {
      articlesAdapter.removeOne(state, action.payload);
    });
  }
});
export const {
  clearArticlesError
} = articlesSlice.actions;
export const articlesSelectors = articlesAdapter.getSelectors(state => state.articles);
export const selectArticlesList = articlesSelectors.selectAll;
export const selectArticlesStatus = state => state.articles.status;
export const selectArticlesError = state => state.articles.error;
export default articlesSlice.reducer;